import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { getTeams } from "@/features/supply-config/teams-api";
import {
  SupplyListCard,
  SupplyPageHeader,
} from "@/features/supply-config/ui/supply-list";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";

export default function Teams() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const { data: teams = [], isLoading, error } = useQuery({
    queryKey: ["teams"],
    queryFn: getTeams,
  });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return teams;
    return teams.filter(
      (t) =>
        t.name.toLowerCase().includes(s) ||
        (t.products ?? []).some((p) => p.name.toLowerCase().includes(s)) ||
        (t.region_rel?.display_name ?? "").toLowerCase().includes(s) ||
        (t.region_rel?.code ?? "").toLowerCase().includes(s) ||
        (t.supported_languages ?? []).some((l) => l.toLowerCase().includes(s)),
    );
  }, [teams, q]);

  return (
    <div className="p-6 space-y-4">
      <SupplyPageHeader
        title="Teams"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate("calendly-groups")}
            >
              Calendly groups
            </Button>
            <HasAccess permission="write">
              <Button className="bg-sidebar" onClick={() => navigate("new")}>
                <Plus className="h-4 w-4 mr-1" /> New Team
              </Button>
            </HasAccess>
          </>
        }
      />

      <SupplyListCard
        isLoading={isLoading}
        error={error}
        isEmpty={filtered.length === 0}
        emptyText={q ? "No teams match your search." : "No teams yet."}
        controls={
          <>
            <span className="text-xs text-gray-400">{filtered.length}</span>
            <div className="relative w-72 max-w-full">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, product, region, language…"
                className="h-8 pl-7 text-sm"
              />
            </div>
          </>
        }
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-left text-xs text-gray-500">
              <th className="py-2 px-4">Name</th>
              <th className="py-2 px-4">Product</th>
              <th className="py-2 px-4">Region</th>
              <th className="py-2 px-4">Languages</th>
              <th className="py-2 px-4">Members</th>
              <th className="py-2 px-4">Managed events</th>
              <th className="py-2 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr
                key={t.id}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(t.id)}
              >
                <td className="py-2 px-4 font-medium">{t.name}</td>
                <td className="py-2 px-4">
                  {t.products?.length
                    ? t.products.map((p) => p.name).join(", ")
                    : "—"}
                </td>
                <td className="py-2 px-4">
                  {t.region_rel
                    ? `${codeToFlag(t.region_rel.code)} ${t.region_rel.code.toUpperCase()}`
                    : "—"}
                </td>
                <td className="py-2 px-4">
                  {t.supported_languages?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {t.supported_languages.map((l) => (
                        <Badge key={l} variant="outline">
                          {l}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-2 px-4">{t.professionals?.length ?? 0}</td>
                <td className="py-2 px-4">{t.managed_events?.length ?? 0}</td>
                <td className="py-2 px-4">
                  <Badge variant={t.status === "ACTIVE" ? "active" : "inactive"}>
                    {t.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SupplyListCard>
    </div>
  );
}
